/**
 * Automatic sign-out after a period of inactivity.
 *
 * Cognito gives us nothing to lean on here: no AccessTokenValidity or
 * IdTokenValidity is configured (so tokens default to 60 minutes) and the
 * deployed RefreshTokenValidity is 30 days, which Amplify spends silently
 * renewing against. A practitioner who signs in on a clinic laptop stays
 * signed in for a month. The timeout is therefore enforced entirely in the
 * client, and these helpers are its arithmetic.
 *
 * Two design points worth keeping:
 *
 * 1. Activity is a WALL-CLOCK TIMESTAMP in localStorage, not a setTimeout.
 *    Timers do not run reliably across a suspended laptop, so "closed the lid
 *    at 4pm, opened it at 9am" has to read as expired — comparing Date.now()
 *    against a stored stamp gets that right where a pending timer would not.
 *
 * 2. It lives in localStorage rather than component state so every window of
 *    the same origin shares one clock. That matters because the PDF export
 *    (bloodwork-test-analysis.tsx) opens a second window running the whole
 *    app, and the practitioner reads and prints there for minutes while the
 *    opener sits untouched. A per-window timer would sign them out mid-print.
 */

/** How long a practitioner may sit idle before being signed out. */
export const IDLE_LIMIT_MS = 60 * 60 * 1000;

/**
 * How long the warning modal is up before the sign-out fires.
 *
 * Five minutes, deliberately generous. Nothing in this app autosaves, so the
 * modal is the only thing standing between a distraction and a half-entered
 * CBC panel — and the practitioner it needs to reach is by definition not
 * looking at the screen, so it has to survive a walk back to the desk, a
 * read, and a decision. Two minutes tested as too tight.
 *
 * The sign-out still lands at IDLE_LIMIT_MS; this only moves the warning
 * earlier (55 minutes in, at the default limit), so it costs no security.
 */
export const WARN_BEFORE_MS = 5 * 60 * 1000;

/** How often the watcher re-checks the clock. */
export const TICK_MS = 5 * 1000;

/**
 * Activity gets stamped at most this often. Without it, `mousemove` would
 * write to localStorage hundreds of times a minute, and every write wakes
 * every other tab's `storage` listener.
 */
export const HEARTBEAT_THROTTLE_MS = 20 * 1000;

export const ACTIVITY_KEY = 'labsmarts_last_activity';

/**
 * Broadcasts a sign-out to every other window of this origin.
 *
 * localStorage rather than sessionStorage because this one has to CROSS
 * windows: `storage` events fire in every other same-origin context, and the
 * originating window is deliberately excluded, so nothing loops.
 *
 * It carries a timestamp rather than a flag because browsers suppress the
 * event when a write does not change the value. Two sign-outs in a row must
 * both be heard.
 */
export const SIGNED_OUT_KEY = 'labsmarts_signed_out';

export function broadcastSignOut(now: number): void {
  try {
    localStorage.setItem(SIGNED_OUT_KEY, String(now));
  } catch (err) {
    // Storage unavailable: other windows keep their own timers as a backstop.
  }
}
export const SIGNOUT_REASON_KEY = 'labsmarts_signout_reason';
export const INACTIVITY_REASON = 'inactivity';

export type IdleState = 'active' | 'warning' | 'expired';

/** Holds the override in MINUTES, as typed in the query string. */
export const IDLE_OVERRIDE_KEY = 'labsmarts_idle_minutes_override';

/**
 * Dev-only escape hatch so nobody has to sit for an hour to test this:
 * load any page with `?idleMinutes=2` and the limit becomes two minutes.
 * `?idleMinutes=off` clears it; so does closing the tab.
 *
 * It STICKS for the tab via sessionStorage, which is the whole point. The
 * query string is read live on every tick, and react-router drops it on the
 * first in-app navigation — without this, clicking through to a client would
 * silently restore the 60-minute limit mid-test and the feature would look
 * broken. sessionStorage also carries it through the reload that a sign-out
 * performs, so the tester lands back on the sign-in screen still overridden.
 *
 * Fails closed exactly the way supplementsVisibility.ts does — only
 * scripts/deploy.js sets REACT_APP_ENV, so any deployed build ignores both the
 * query param and the stored value, and a plain `npm run build` does too. The
 * override can only ever SHORTEN the limit, so neither a stray URL nor a
 * tampered sessionStorage value can extend a real session.
 */
function overrideEnabled(): boolean {
  if (process.env.REACT_APP_ENV) return false;
  return process.env.NODE_ENV !== 'production';
}

/** Accepts only a positive number of minutes shorter than the real limit. */
function parseOverride(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const minutes = Number(raw);
  if (!isFinite(minutes) || minutes <= 0) return undefined;
  const ms = minutes * 60 * 1000;
  return ms < IDLE_LIMIT_MS ? ms : undefined;
}

function idleLimitOverrideMs(): number | undefined {
  if (!overrideEnabled()) return undefined;
  if (typeof window === 'undefined' || !window.location) return undefined;

  const fromUrl = new URLSearchParams(window.location.search).get('idleMinutes');

  if (fromUrl === 'off') {
    try {
      sessionStorage.removeItem(IDLE_OVERRIDE_KEY);
    } catch (err) {
      // Nothing to do; the tab closing clears it anyway.
    }
    return undefined;
  }

  const parsed = parseOverride(fromUrl);
  if (parsed !== undefined) {
    try {
      // Store the raw MINUTES, the same unit parseOverride reads back below.
      sessionStorage.setItem(IDLE_OVERRIDE_KEY, fromUrl as string);
    } catch (err) {
      // Storage unavailable: the override still works until the first
      // in-app navigation drops the query string.
    }
    return parsed;
  }

  // No usable param on this URL — fall back to whatever this tab was given
  // earlier. Re-validated on read, so a hand-edited value cannot lengthen it.
  try {
    return parseOverride(sessionStorage.getItem(IDLE_OVERRIDE_KEY));
  } catch (err) {
    return undefined;
  }
}

export function idleLimitMs(): number {
  return idleLimitOverrideMs() || IDLE_LIMIT_MS;
}

/**
 * Capture the override at import time, before anything can navigate.
 *
 * Without this the first read happens on the watcher's first tick, five
 * seconds after mount — and clicking any nav link inside that window drops the
 * query string before it was ever persisted, silently reverting to 60 minutes.
 * Reading once here closes the race: by the time the app can navigate, the
 * value is already in sessionStorage.
 */
idleLimitOverrideMs();

/**
 * The warning window has to fit inside the limit — a 1-minute dev override
 * with a 2-minute warning would otherwise put the modal up before the clock
 * even starts. Halve it in that case so the override stays usable.
 */
export function warnBeforeMs(limitMs: number = idleLimitMs()): number {
  return Math.min(WARN_BEFORE_MS, Math.floor(limitMs / 2));
}

/**
 * localStorage throws rather than no-ops in a few real configurations (Safari
 * private browsing, storage-blocking extensions). None of them should take the
 * app down, so both accessors swallow and let the caller fall back to "active"
 * — a broken clock must not lock a practitioner out mid-consultation.
 */
export function readLastActivity(): number | undefined {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    if (!raw) return undefined;
    const parsed = Number(raw);
    return isFinite(parsed) && parsed > 0 ? parsed : undefined;
  } catch (err) {
    return undefined;
  }
}

export function writeLastActivity(now: number): void {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(now));
  } catch (err) {
    // See above: a failed stamp degrades to "never idle", not to a lockout.
  }
}

/**
 * Ends a dev override, so one `?idleMinutes=` load buys one test cycle.
 *
 * Called on every sign-out. Without it the override outlives the sign-out it
 * just caused and immediately re-arms: you get kicked out, land on the sign-in
 * screen still overridden, sign back in and get kicked out again minutes
 * later. Making the value survive the reload was meant to save re-typing the
 * URL; in practice it turned normal local development into a loop.
 */
export function clearIdleOverride(): void {
  try {
    sessionStorage.removeItem(IDLE_OVERRIDE_KEY);
  } catch (err) {
    // Storage unavailable, so there is nothing sticky to clear anyway.
  }
}

export function clearLastActivity(): void {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
  } catch (err) {
    // Nothing to do — a stale stamp is harmless, since the watcher re-stamps
    // on mount and the next sign-in mounts a fresh one.
  }
}

/**
 * The whole decision, as a pure function.
 *
 * A missing stamp reads as `active`: it means the key was cleared or storage
 * is unavailable, and the honest response to "we don't know when they last
 * did anything" is to leave them alone, not to sign them out. The watcher
 * stamps on mount, so the unknown state resolves within one tick anyway.
 */
export function idleStateFor(
  lastActivity: number | undefined,
  now: number,
  limitMs: number = idleLimitMs(),
): IdleState {
  if (lastActivity === undefined) return 'active';

  // A stamp in the future means another window's clock is skewed. Treat it as
  // fresh rather than letting the subtraction go negative and expire someone.
  const idleFor = Math.max(0, now - lastActivity);

  if (idleFor >= limitMs) return 'expired';
  if (idleFor >= limitMs - warnBeforeMs(limitMs)) return 'warning';
  return 'active';
}

export function msUntilSignOut(
  lastActivity: number | undefined,
  now: number,
  limitMs: number = idleLimitMs(),
): number {
  if (lastActivity === undefined) return limitMs;
  return Math.max(0, lastActivity + limitMs - now);
}

/** `m:ss`, for the countdown in the warning modal. */
export function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
